import { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { Input } from '../ui/Input'
import authService from '../../services/authService'
import { toast } from 'react-hot-toast'

interface LoginFormValues {
  email: string
  password: string
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Correo electrónico inválido')
    .required('Este campo es obligatorio'),
  password: Yup.string().required('Este campo es obligatorio'),
})

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>
  errorMessage?: string
}

export const LoginForm = ({ onSubmit, errorMessage }: LoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleCloseForgotPasswordModal = () => {
    setShowPasswordModal(false)
    setForgotPasswordEmail('')
    setEmailSent(false)
    setSendingEmail(false)
  }

  const sendPasswordResetEmail = async () => {
    const emailToUse = forgotPasswordEmail.trim()
    setSendingEmail(true)

    try {
      const response = await authService.forgotPassword(emailToUse)

      if (response.success) {
        setEmailSent(true)
        toast.success('Contraseña enviada a tu correo')
      } else {
        toast.error(response.message || 'Error al enviar el código')
      }
    } catch (error: any) {
      console.error('Error sending verification code:', error)
      toast.error(
        error.response?.data?.message ||
          'Error al enviar el código de verificación'
      )
    } finally {
      setSendingEmail(false)
    }
  }

  const renderForm = () => (
    <Formik
      initialValues={{ email: '', password: '' }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Correo electrónico
            </label>
            <Field
              type="email"
              name="email"
              placeholder="ejemplo@correo.com"
              className={`w-full px-3 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                (errors.email && touched.email) ||
                errorMessage === 'email_not_found'
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />
            <ErrorMessage
              name="email"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
            {errorMessage === 'email_not_found' && (
              <div className="text-red-500 text-sm mt-1">
                *Este correo no está registrado.
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Field
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="••••••••"
                className={`w-full px-3 py-2.5 sm:py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                  errors.password && touched.password
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <ErrorMessage
              name="password"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
            {errorMessage === 'wrong_password' && (
              <div className="text-red-500 text-sm mt-1">
                *La contraseña es incorrecta.
              </div>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            size="default"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="w-full"
          >
            Entrar
          </Button>

          <button
            type="button"
            onClick={() => setShowPasswordModal(true)}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
            disabled={sendingEmail}
          >
            Olvidé mi contraseña
          </button>

          <Modal
            isOpen={showPasswordModal}
            onClose={() => setShowPasswordModal(false)}
            size="sm"
            closeOnOverlay={false}
          >
            <div className="p-8 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Recuperación de contraseña
              </h2>

              {sendingEmail ? (
                <div className="flex flex-col items-center justify-center mb-6">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-gray-600">
                    Enviando contraseña de recuperación...
                  </p>
                </div>
              ) : emailSent ? (
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Hemos enviado una contraseña a su correo electrónico:{' '}
                    <strong>{forgotPasswordEmail}</strong>
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Por favor, verifique sus mensajes e ingrese la contraseña
                    para continuar.
                  </p>
                  <button
                    type="button"
                    onClick={sendPasswordResetEmail}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                    disabled={sendingEmail}
                  >
                    ¿No recibiste la contraseña de recuperación? Reenviar
                  </button>
                </div>
              ) : (
                <div className="mb-2">
                  <div className="mb-6">
                    <Input
                      placeholder="Ingresa su correo electrónico"
                      value={forgotPasswordEmail}
                      onChange={e => {
                        setForgotPasswordEmail(e.target.value)
                      }}
                      className="text-center text-lg font-medium"
                      disabled={sendingEmail}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="primary"
                    onClick={sendPasswordResetEmail}
                    className="w-full mb-4"
                    disabled={sendingEmail || forgotPasswordEmail.trim() === ''}
                  >
                    Enviar
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseForgotPasswordModal}
                  className="w-full"
                >
                  Volver
                </Button>
              </div>
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  )

  return (
    <>
      {/* Mobile Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        {/* Mobile Header - Solo gradiente */}
        <div
          className="h-40 sm:h-48 flex items-center justify-center relative"
          style={{
            background:
              'linear-gradient(180deg, #1789CF 0%, #02298A 84.79%, #021B79 100%)',
            minHeight: '160px',
          }}
        >
          {/* Logo centrado - más grande */}
          <div className="text-center z-10">
            <img
              src="/assets/olr-logo.png"
              alt="OLR Leasing"
              className="filter brightness-0 invert w-32 sm:w-40 h-auto mx-auto"
            />
          </div>
        </div>

        {/* Mobile Form section - menos padding top */}
        <div className="flex-1 bg-white flex items-start justify-center pt-6 sm:pt-8 px-4 sm:px-6 pb-6">
          <div className="w-full max-w-md">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-left">
              Inicio de sesión
            </h2>
            {renderForm()}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex min-h-screen">
        {/* Left side - SVG background with logo */}
        <div
          className="flex-1 flex items-center justify-center relative"
          style={{
            backgroundImage: 'url(/assets/bg-login.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
          }}
        >
          <div className="absolute left-8 top-1/2 transform -translate-y-1/2 z-10">
            <img
              src="/assets/olr-logo.png"
              alt="OLR Leasing"
              className="filter brightness-0 invert w-48 md:w-64 lg:w-80 h-auto m-[20%]"
            />
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="flex-1 bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Inicio de sesión
            </h2>
            {renderForm()}
          </div>
        </div>
      </div>
    </>
  )
}
